import {
  GroupedTable,
  GroupedTableColumn,
} from "@/components/GroupedTable/GroupedTable";
import { Page, PageBody, PageHeader } from "@/components/app-layout";
import { truncateLength } from "@/components/app-layout/consts";
import { UserDisplayName } from "@/components/display/UserDisplayName";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Column, Row } from "@/components/ui/flex";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { InviteToOrganizationDialog } from "@/components/organization/InviteToOrganizationDialog";
import { CreateTeamGroupDialog } from "@/components/group/CreateTeamGroupDialog";
import { CreateUserButton } from "@/components/user/CreateUserButton";
import { ImpersonateUserButton } from "@/components/user/ImpersonateUserButton";
import { GroupRoleSelection } from "@/components/user/RoleSelection";
import { UserBulkActions } from "@/components/user/UserBulkActions";
import { useAuthDetails } from "@/context/auth-context";
import { useGroupUserMeData } from "@/data/use-group-user-me-data";
import { useGroupData } from "@/data/use-groups-data";
import { GroupUser, useUsersData } from "@/data/use-users-data";
import { useGroupUuid } from "@/hooks/use-group-uuid.tsx";
import { GroupRole } from "@/lib/consts";
import { groupByColumn, sortFunctions } from "@/lib/groupBy";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFaceDisguise,
  faTrash,
} from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { toast } from "sonner";
import { useState } from "react";

export default function UsersPage() {
  const groupUuid = useGroupUuid();
  const { group } = useGroupData(groupUuid);
  const { users, deleteUser, updateUserRole } = useUsersData(groupUuid);
  const { organizationRole, teamGroupAdmin } = useGroupUserMeData(groupUuid);
  const [isCreatingTeam, setIsCreatingTeam] = useState(false);
  const [isInviting, setIsInviting] = useState(false);

  const authDetails = useAuthDetails();
  const isSuper = authDetails?.role === "super";
  const canCreateTeam = isSuper || organizationRole === "admin";
  const canInviteToOrganization =
    isSuper || organizationRole === "admin" || teamGroupAdmin;

  if (group?.personal) {
    return (
      <Page>
        <PageHeader>
          <PageHeader.Title>workspace User Management</PageHeader.Title>
        </PageHeader>
        <PageBody>
          <Column>
            <p>
              My workspace is only for you. You cannot add or remove members
              here. Share content with teammates and people from a team
              workspace using Share.
            </p>
            {(canCreateTeam || canInviteToOrganization) && (
              <Row className="gap-2">
                {canCreateTeam && (
                  <Button onClick={() => setIsCreatingTeam(true)}>
                    Create team workspace
                  </Button>
                )}
                {canInviteToOrganization && (
                  <Button onClick={() => setIsInviting(true)}>
                    Invite to organization
                  </Button>
                )}
              </Row>
            )}
          </Column>
        </PageBody>
        {canCreateTeam && (
          <CreateTeamGroupDialog
            open={isCreatingTeam}
            onOpenChange={setIsCreatingTeam}
            organizationUuid={group.organizationUuid}
          />
        )}
        {canInviteToOrganization && (
          <InviteToOrganizationDialog
            open={isInviting}
            onOpenChange={setIsInviting}
            organizationUuid={group.organizationUuid}
          />
        )}
      </Page>
    );
  }

  const shouldRenderCheckedActions = authDetails?.role === "super";

  const groupedUsers = groupByColumn(
    users,
    "groupRole",
    sortFunctions.alphabetical("email"),
  );

  const columns: GroupedTableColumn<GroupUser>[] = [
    { key: "name", label: "Name" },
    { key: "email", label: "Email" },
    { key: "groupRole", label: "Group Role" },
  ];

  return (
    <Page>
      <PageHeader>
        <PageHeader.Title>Group User Management</PageHeader.Title>
      </PageHeader>
      <PageBody>
        <Column>
          <p>
            This is where you manage who has access to your workspace. You can
            add new team members, remove old ones, and set their roles.
          </p>
        </Column>
        <Row className="gap-2">
          <CreateUserButton groupUuid={groupUuid} />
          {canCreateTeam && group && (
            <Button onClick={() => setIsCreatingTeam(true)}>
              Create team workspace
            </Button>
          )}
          {canInviteToOrganization && group && (
            <Button onClick={() => setIsInviting(true)}>
              Invite to organization
            </Button>
          )}
        </Row>
        <GroupedTable
          data={groupedUsers}
          columns={columns}
          itemIdKey="uuid"
          isSearchable
          isSortable
          renderCell={{
            name: (_, row) => <UserDisplayName user={row} />,
            email: (value) =>
              String(value).length > truncateLength ? (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>{`${String(value).slice(0, truncateLength)}…`}</span>
                    </TooltipTrigger>
                    <TooltipContent>{String(value)}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                String(value)
              ),
            groupRole: (value, row) => (
              <GroupRoleSelection
                value={value as GroupRole}
                onChange={(role) =>
                  updateUserRole(
                    { userUuid: row.uuid, role },
                    {
                      onSuccess: (data) => {
                        if (data.response.ok) {
                          toast.success("User role updated successfully");
                        } else {
                          toast.error("Failed to update user role");
                        }
                      },
                      onError: () => {
                        toast.error("Failed to update user role");
                      },
                    },
                  )
                }
              />
            ),
          }}
          renderActions={({ row }) => (
            <UserRowActions
              row={row}
              deleteUser={() =>
                deleteUser(row.uuid, {
                  onSuccess: (data) => {
                    if (data.response.ok) {
                      toast.success("User removed from group successfully");
                    } else {
                      toast.error("Failed to remove user");
                    }
                  },
                  onError: () => {
                    toast.error("Failed to remove user");
                  },
                })
              }
            />
          )}
          emptyMessage="No users found"
          checkedActions={(selectedItems) =>
            shouldRenderCheckedActions ? (
              <UserBulkActions
                selectedItems={selectedItems}
                deleteUser={deleteUser}
                variant="group"
              />
            ) : undefined
          }
        />
      </PageBody>
      {canCreateTeam && group && (
        <CreateTeamGroupDialog
          open={isCreatingTeam}
          onOpenChange={setIsCreatingTeam}
          organizationUuid={group.organizationUuid}
        />
      )}
      {canInviteToOrganization && group && (
        <InviteToOrganizationDialog
          open={isInviting}
          onOpenChange={setIsInviting}
          organizationUuid={group.organizationUuid}
        />
      )}
    </Page>
  );
}

function UserRowActions({
  row,
  deleteUser,
}: {
  row: GroupUser;
  deleteUser: () => Promise<unknown>;
}) {
  return (
    <Row>
      <ImpersonateUserButton user={row}>
        <Button
          variant="ghost"
          size="sm"
          className="hover:bg-destructive hover:text-destructive-foreground"
          title="Impersonate"
        >
          <FontAwesomeIcon icon={faFaceDisguise} />
        </Button>
      </ImpersonateUserButton>

      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="hover:bg-destructive hover:text-destructive-foreground"
            title="Remove"
          >
            <FontAwesomeIcon icon={faTrash} />
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {row.name || row.email}?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently remove this
              user from the group.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={deleteUser}
              asChild
            >
              <Button size="sm">Remove</Button>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Row>
  );
}
